import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import Icon from '../common/containers/icon';
import { SCREENSHOT_ATTACHED_LABEL, SCREENSHOT_CAPTURING_LABEL, SCREENSHOT_ERROR_LABEL, SCREENSHOT_MISSING_LABEL, SCREENSHOT_REMOVE_LABEL, SCREENSHOT_RETAKE_LABEL } from './feedbackCopy';

/**
 * Shows the auto-captured viewport thumbnail for a problem report, with remove / retake.
 * Annotation is intentionally out of scope for this pass.
 */
const FeedbackScreenshot = ({
  previewUrl: _previewUrl = null,
  isCapturing: _isCapturing = false,
  hasError: _hasError = false,
  onRemove,
  onRetake
}) => {
  const [objectUrl, setObjectUrl] = useState(null);
  useEffect(() => {
    if (!_previewUrl) {
      setObjectUrl(null);
      return undefined;
    }
    if (typeof _previewUrl === 'string') {
      setObjectUrl(_previewUrl);
      return undefined;
    }

    // Blob preview from capture — revoke on change/unmount so we do not leak URLs.
    // jsdom (and some older browsers) may not implement createObjectURL.
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
      setObjectUrl(null);
      return undefined;
    }
    const nextUrl = URL.createObjectURL(_previewUrl);
    setObjectUrl(nextUrl);
    return () => {
      if (typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(nextUrl);
      }
    };
  }, [_previewUrl]);
  const retakeButton = /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "t-feedback-screenshot__action",
    onClick: onRetake
  }, SCREENSHOT_RETAKE_LABEL);
  if (_isCapturing) {
    return /*#__PURE__*/React.createElement("div", {
      className: "t-feedback-screenshot t-feedback-screenshot--capturing",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "t-feedback-screenshot__icon",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "image"
    })), /*#__PURE__*/React.createElement("span", {
      className: "t-feedback-screenshot__text"
    }, SCREENSHOT_CAPTURING_LABEL));
  }
  if (!objectUrl) {
    const isError = _hasError;
    const modifier = isError ? 't-feedback-screenshot--error' : 't-feedback-screenshot--missing';
    return /*#__PURE__*/React.createElement("div", {
      className: `t-feedback-screenshot ${modifier}`
    }, /*#__PURE__*/React.createElement("div", {
      className: "t-feedback-screenshot__row"
    }, /*#__PURE__*/React.createElement("span", {
      className: "t-feedback-screenshot__icon",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "image"
    })), /*#__PURE__*/React.createElement("span", {
      className: "t-feedback-screenshot__text",
      role: isError ? 'status' : undefined
    }, isError ? SCREENSHOT_ERROR_LABEL : SCREENSHOT_MISSING_LABEL)), /*#__PURE__*/React.createElement("div", {
      className: "t-feedback-screenshot__actions"
    }, retakeButton));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "t-feedback-screenshot t-feedback-screenshot--attached"
  }, /*#__PURE__*/React.createElement("div", {
    className: "t-feedback-screenshot__preview-wrap"
  }, /*#__PURE__*/React.createElement("img", {
    className: "t-feedback-screenshot__preview",
    src: objectUrl,
    alt: SCREENSHOT_ATTACHED_LABEL
  })), /*#__PURE__*/React.createElement("div", {
    className: "t-feedback-screenshot__row"
  }, /*#__PURE__*/React.createElement("span", {
    className: "t-feedback-screenshot__icon",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "image"
  })), /*#__PURE__*/React.createElement("span", {
    className: "t-feedback-screenshot__text"
  }, SCREENSHOT_ATTACHED_LABEL)), /*#__PURE__*/React.createElement("div", {
    className: "t-feedback-screenshot__actions"
  }, retakeButton, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "t-feedback-screenshot__action t-feedback-screenshot__action--danger",
    onClick: onRemove
  }, SCREENSHOT_REMOVE_LABEL)));
};
FeedbackScreenshot.propTypes = {
  // data URL string or Blob from captureViewportScreenshot
  previewUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Blob)]),
  isCapturing: PropTypes.bool,
  hasError: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
  onRetake: PropTypes.func.isRequired
};
export default FeedbackScreenshot;